import { useCallback, useEffect, useState } from "react";
import { useMemberPlanState } from "./useMemberPlanState";
import { useMemberStatus } from "./useMemberStatus";
import { useActiveWorkout } from "./useActiveWorkout";
import {
  localTrainingDate,
  scheduledMemberDay,
  type MemberTrainingPlan,
  type MemberPlanDay,
  type DailyTrainingOverride,
} from "@/lib/memberTrainingDay";

export function useTrainingDate() {
  const [today, setToday] = useState(localTrainingDate);
  useEffect(() => {
    const tick = () => setToday(localTrainingDate());
    const timer = window.setInterval(tick, 10000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  return today;
}
export function useTrainingSchedule() {
  const { member } = useMemberStatus();
  const { activeWorkout } = useActiveWorkout();
  const [stored, setStored] = useMemberPlanState();
  const today = useTrainingDate();
  const plan = member.isMember ? stored : null;
  const requireEditable = useCallback(() => {
    if (!member.isMember || !stored)
      throw new Error("会员专属，请先生成定制计划");
    if (activeWorkout.active) throw new Error("请先完成或结束当前训练");
  }, [member.isMember, stored, activeWorkout.active]);
  const saveSchedule = (days: MemberPlanDay[]) => {
    requireEditable();
    setStored((previous) => {
      if (!previous || previous.id !== stored?.id)
        throw new Error("计划已更新，请重新打开排期面板");
      return {
        ...previous,
        scheduleMode: "calendar",
        weekDays: days.map((day) => ({
          ...day,
          virtualId: `member-schedule-${crypto.randomUUID()}`,
        })),
        weeklyHours:
          Math.round(days.reduce((sum, d) => sum + d.durationMin, 0) / 6) / 10,
      };
    });
  };
  const replaceToday = (
    day: MemberPlanDay,
    kind: DailyTrainingOverride["kind"],
  ) => {
    requireEditable();
    setStored((previous) => {
      if (!previous) return previous;
      if (previous.id !== stored?.id)
        throw new Error("计划已更新，请重新打开换课面板");
      const originalDay =
        previous.dailyOverrides?.[today]?.originalDay ||
        scheduledMemberDay(previous);
      if (!originalDay) return previous;
      const id = crypto.randomUUID();
      const replacement: DailyTrainingOverride = {
        id,
        date: today,
        kind,
        originalDay,
        day: {
          ...day,
          dayOfWeek: originalDay.dayOfWeek,
          virtualId: `member-swap-${id}`,
        },
      };
      return {
        ...previous,
        dailyOverrides: { ...previous.dailyOverrides, [today]: replacement },
      };
    });
  };
  return {
    plan,
    today,
    override: plan?.dailyOverrides?.[today],
    saveSchedule,
    replaceToday,
    disabled: !plan || activeWorkout.active,
  };
}
